import { useCallback } from "react";
import imageCompression from "browser-image-compression";

export const useImageProcessor = () => {
  const processImage = useCallback(async (file, onProgress) => {
    // Check if this is a mobile device
    const isMobile =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isCamera = file.name?.startsWith("image") || file.name === "blob";

    // Validation
    if (!file.type.startsWith("image/")) {
      throw new Error("Please select an image file");
    }

    if (file.size > 20 * 1024 * 1024) {
      throw new Error("Image is too large (max 20MB)");
    }

    // Aggressive compression settings for mobile
    let compressionOptions;

    if (isMobile) {
      // Much more aggressive compression for mobile
      compressionOptions = {
        maxWidthOrHeight: isCamera ? 1200 : 1600, // Smaller for camera shots
        maxSizeMB: isCamera ? 0.5 : 1, // Very small for camera shots
        useWebWorker: true,
        fileType: "image/jpeg",
        initialQuality: isCamera ? 0.6 : 0.7, // Lower quality for camera
        alwaysKeepResolution: false,
        exifOrientation: 1, // Reset EXIF orientation
      };
    } else {
      // Standard compression for desktop
      compressionOptions = {
        maxWidthOrHeight: 1920,
        maxSizeMB: 2,
        useWebWorker: true,
        fileType: "image/jpeg",
        initialQuality: 0.8,
      };
    }

    // Processing with progress
    const options = {
      ...compressionOptions,
      onProgress: (progress) => {
        onProgress({
          stage: "processing",
          progress: progress * 0.8, // Reserve 20% for other operations
        });
      },
    };

    try {
      onProgress({ stage: "processing", progress: 0 });

      // For mobile camera captures, do additional pre-processing
      if (isMobile && isCamera && file.size > 5 * 1024 * 1024) {
        // First pass - reduce size dramatically
        const preCompressed = await imageCompression(file, {
          maxWidthOrHeight: 800,
          maxSizeMB: 1,
          useWebWorker: false, // Don't use web worker for pre-compression
          fileType: "image/jpeg",
          initialQuality: 0.5,
        });

        // Use pre-compressed file for main compression
        file = preCompressed;
      }

      const compressedBlob = await imageCompression(file, options);

      // Create proper filename with correct extension
      const originalExtension =
        file.name?.split(".").pop()?.toLowerCase() || "jpg";
      let finalExtension = originalExtension;

      if (
        options.fileType === "image/jpeg" &&
        !["jpg", "jpeg"].includes(originalExtension)
      ) {
        finalExtension = "jpg";
      }

      // Create meaningful filename
      const timestamp = Date.now();
      let baseName;

      if (isMobile && isCamera) {
        baseName = `mobile_camera_${timestamp}`;
      } else if (file.name && file.name !== "blob") {
        baseName = file.name.replace(/\.[^/.]+$/, "") + "_compressed";
      } else {
        baseName = `upload_${timestamp}`;
      }

      const newFileName = `${baseName}.${finalExtension}`;

      // Convert Blob to File with proper name and type
      const compressedFile = new File([compressedBlob], newFileName, {
        type: compressedBlob.type || options.fileType,
        lastModified: Date.now(),
      });

      const result = {
        originalFile: file,
        compressedFile,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        compressionRatio: (
          ((file.size - compressedFile.size) / file.size) *
          100
        ).toFixed(1),
      };

      console.log("Compression complete:", {
        originalSize: result.originalSize,
        compressedSize: result.compressedSize,
        compressionRatio: result.compressionRatio + "%",
        finalFilename: compressedFile.name,
        finalType: compressedFile.type,
      });

      // Check if compression was successful enough for mobile
      if (isMobile && compressedFile.size > 2 * 1024 * 1024) {
        console.warn(
          "Mobile file still large after compression:",
          compressedFile.size
        );
        // Could add additional compression here if needed
      }

      onProgress({ stage: "complete", progress: 1 });

      return result;
    } catch (error) {
      console.error("Image processing error:", error);
      onProgress({ stage: "error", error: error.message });
      throw error;
    }
  }, []);

  return { processImage };
};
